import { unlink } from "node:fs/promises";
import { join, resolve } from "node:path";
import { z } from "zod";
import { UrlSchema, type Url } from "../data-model/primitives.js";
import { atomicWriteJson, isEnoent, readJsonWithSchema } from "./_fs.js";

export interface CachedResponse {
  readonly status: number;
  readonly headers: Readonly<Record<string, string>>;
  readonly body: string;
  readonly url: Url;
  readonly fetchedAt: Date;
  readonly etag?: string;
  readonly lastModified?: string;
}

export interface CacheStore {
  get(key: string): Promise<CachedResponse | null>;
  set(key: string, value: CachedResponse): Promise<void>;
  delete(key: string): Promise<void>;
}

export class MemoryCacheStore implements CacheStore {
  readonly #map = new Map<string, CachedResponse>();
  async get(key: string): Promise<CachedResponse | null> {
    return this.#map.get(key) ?? null;
  }
  async set(key: string, value: CachedResponse): Promise<void> {
    this.#map.set(key, value);
  }
  async delete(key: string): Promise<void> {
    this.#map.delete(key);
  }
}

const SerializedEntrySchema = z.object({
  v: z.literal(1),
  status: z.number().int(),
  headers: z.record(z.string()),
  body: z.string(),
  url: UrlSchema,
  fetchedAt: z.string().datetime(),
  etag: z.string().optional(),
  lastModified: z.string().optional(),
});

type SerializedEntry = z.infer<typeof SerializedEntrySchema>;

export class FilesystemCacheStore implements CacheStore {
  readonly #dir: string;

  constructor(dir: string) {
    this.#dir = resolve(dir);
  }

  async get(key: string): Promise<CachedResponse | null> {
    const raw = await readJsonWithSchema(this.#pathFor(key), SerializedEntrySchema);
    if (raw === null) return null;
    return {
      status: raw.status,
      headers: raw.headers,
      body: raw.body,
      url: raw.url,
      fetchedAt: new Date(raw.fetchedAt),
      ...(raw.etag !== undefined ? { etag: raw.etag } : {}),
      ...(raw.lastModified !== undefined ? { lastModified: raw.lastModified } : {}),
    };
  }

  async set(key: string, value: CachedResponse): Promise<void> {
    const serialized: SerializedEntry = {
      v: 1,
      status: value.status,
      headers: { ...value.headers },
      body: value.body,
      url: value.url,
      fetchedAt: value.fetchedAt.toISOString(),
      ...(value.etag !== undefined ? { etag: value.etag } : {}),
      ...(value.lastModified !== undefined ? { lastModified: value.lastModified } : {}),
    };
    await atomicWriteJson(this.#pathFor(key), serialized);
  }

  async delete(key: string): Promise<void> {
    try {
      await unlink(this.#pathFor(key));
    } catch (err) {
      if (!isEnoent(err)) throw err;
    }
  }

  #pathFor(key: string): string {
    if (!/^[A-Za-z0-9_-]+$/.test(key)) {
      throw new Error(`FilesystemCacheStore: unsafe cache key ${JSON.stringify(key)}`);
    }
    return join(this.#dir, `${key}.json`);
  }
}
